(() => {
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const mobileQuery = window.matchMedia('(max-width: 749px)');
  const tabletQuery = window.matchMedia('(min-width: 750px) and (max-width: 989px)');

  const initPagedRow = (root) => {
    if (root.dataset.tataPagedInit === 'true') return;

    const slider = root.closest('.tata-home-product-row__slider, .tata-related-products__layout');
    const track = root.querySelector('.tata-home-product-row__track');
    if (!slider || !track) return;

    const isHomeProductRow = root.hasAttribute('data-tata-home-row-paged');

    root.dataset.tataPagedInit = 'true';

    const prevButton = slider.querySelector('[data-tata-home-row-nav="prev"]');
    const nextButton = slider.querySelector('[data-tata-home-row-nav="next"]');
    let page = 1;
    let isAnimating = false;

    const isMobile = () => mobileQuery.matches;
    const isTablet = () => tabletQuery.matches;

    const getPageTotal = () => {
      if (isHomeProductRow && isMobile()) {
        return parseInt(root.dataset.pageTotalMobile, 10) || 1;
      }
      if (isHomeProductRow && isTablet()) {
        return parseInt(root.dataset.pageTotalTablet, 10) || 1;
      }
      if (isHomeProductRow) {
        return parseInt(root.dataset.pageTotalDesktop, 10) || parseInt(root.dataset.pageTotal, 10) || 1;
      }
      if (isMobile()) return 1;
      return parseInt(root.dataset.pageTotal, 10) || 1;
    };

    const getMaxHtmlPages = () => {
      if (!isHomeProductRow) return track.querySelectorAll('.tata-home-product-row__page').length;
      const cap = isMobile()
        ? parseInt(root.dataset.productCapMobile, 10) || 0
        : parseInt(root.dataset.productCapDesktop, 10) || 0;
      return Math.max(1, Math.ceil(cap / 2));
    };

    const syncPageVisibility = () => {
      if (!isHomeProductRow) return;
      const maxHtmlPages = getMaxHtmlPages();
      track.querySelectorAll('.tata-home-product-row__page').forEach((pageEl, index) => {
        pageEl.hidden = index >= maxHtmlPages;
      });
    };

    const setNavConcealed = (button, concealed) => {
      if (!button) return;
      button.classList.toggle('tata-home-product-row__nav-button--concealed', concealed);
      button.disabled = concealed;
      button.setAttribute('aria-hidden', concealed ? 'true' : 'false');
      button.tabIndex = concealed ? -1 : 0;
    };

    const syncButtons = () => {
      const pageTotal = getPageTotal();
      setNavConcealed(prevButton, page <= 1);
      setNavConcealed(nextButton, page >= pageTotal);
    };

    const getSlideStepPx = () => {
      const viewport = root.querySelector('.tata-home-product-row__viewport');
      const pages = [...track.querySelectorAll('.tata-home-product-row__page')].filter((el) => !el.hidden);
      if (pages.length >= 2) {
        return pages[1].offsetLeft - pages[0].offsetLeft;
      }
      return viewport?.clientWidth ?? 0;
    };

    const applyTransform = (animate) => {
      if (!isHomeProductRow && isMobile()) {
        track.style.transform = 'none';
        track.style.transition = 'none';
        return;
      }
      if (!animate || reducedMotion) {
        track.style.transition = 'none';
      } else {
        track.style.transition = '';
      }

      if (isHomeProductRow) {
        const offsetPx = (page - 1) * getSlideStepPx();
        track.style.transform = `translate3d(-${offsetPx}px, 0, 0)`;
      } else {
        const offset = (page - 1) * 100;
        track.style.transform = `translate3d(-${offset}%, 0, 0)`;
      }

      if (!animate || reducedMotion) {
        track.offsetHeight;
        track.style.transition = '';
      }
    };

    // カード画像は data-tata-defer-src / srcset で出力されている（card-product の defer_media）。
    // 行が画面に近づいたら「表示中のページ＋次の 1 画面分」だけ本来の属性に戻して読み込む
    let isRowNear = false;

    const restoreDeferredImage = (img) => {
      const { tataDeferSrcset: srcset, tataDeferSrc: src } = img.dataset;
      if (srcset) img.srcset = srcset;
      if (src) img.src = src;
      img.removeAttribute('data-tata-defer-srcset');
      img.removeAttribute('data-tata-defer-src');
    };

    const loadDeferredImages = () => {
      if (!isRowNear) return;
      const viewport = root.querySelector('.tata-home-product-row__viewport');
      const step = getSlideStepPx();
      const visiblePages = step > 0 && viewport ? Math.max(1, Math.round(viewport.clientWidth / step)) : 1;
      const loadUntil = page - 1 + visiblePages * 2;
      [...track.querySelectorAll('.tata-home-product-row__page')]
        .filter((el) => !el.hidden)
        .forEach((pageEl, index) => {
          if (index >= loadUntil) return;
          pageEl.querySelectorAll('img[data-tata-defer-src], img[data-tata-defer-srcset]').forEach(restoreDeferredImage);
        });
    };

    const watchRowVisibility = () => {
      if (!track.querySelector('img[data-tata-defer-src], img[data-tata-defer-srcset]')) return;
      if (!('IntersectionObserver' in window)) {
        isRowNear = true;
        loadDeferredImages();
        return;
      }
      const observer = new IntersectionObserver(
        (entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          observer.disconnect();
          isRowNear = true;
          loadDeferredImages();
        },
        { rootMargin: '300px 0px' }
      );
      observer.observe(root);
    };

    const goToPage = (nextPage) => {
      if (!isHomeProductRow && isMobile()) return;
      const pageTotal = getPageTotal();
      const target = Math.min(pageTotal, Math.max(1, nextPage));
      if (target === page || isAnimating) return;

      isAnimating = !reducedMotion;
      page = target;
      root.dataset.currentPage = String(page);
      loadDeferredImages();
      applyTransform(true);
      syncButtons();

      if (reducedMotion) {
        isAnimating = false;
        syncButtons();
        return;
      }

      const onEnd = (event) => {
        if (event.propertyName !== 'transform') return;
        track.removeEventListener('transitionend', onEnd);
        isAnimating = false;
        syncButtons();
      };
      track.addEventListener('transitionend', onEnd);
    };

    const onBreakpointChange = () => {
      isAnimating = false;
      page = 1;
      root.dataset.currentPage = '1';
      syncPageVisibility();
      applyTransform(false);
      syncButtons();
      loadDeferredImages();
    };

    prevButton?.addEventListener('click', () => goToPage(page - 1));
    nextButton?.addEventListener('click', () => goToPage(page + 1));
    mobileQuery.addEventListener('change', onBreakpointChange);
    tabletQuery.addEventListener('change', onBreakpointChange);

    onBreakpointChange();
    watchRowVisibility();
  };

  window.TataPagedProductRow = {
    init(scope = document) {
      scope.querySelectorAll('[data-tata-home-row-paged], [data-tata-related-paged]').forEach((root) => {
        initPagedRow(root);
      });
    },
  };

  window.TataPagedProductRow.init();
})();
